import React, { useState } from 'react';
import { Box, Grid, Typography, MenuItem } from '@mui/material';
import dayjs from 'dayjs';
import { validateOCRForm, getAge } from '../utils/validation';
import { 
    TextField,
    CustomCheckbox,
    CustomButton,
    CustomSelect
} from '../../../../v2/MUI_custom_component';
import { DatePicker } from '../../../../v2/MUI_custom_component';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";

const ReusableManualEntryForm = ({ 
    onSave, 
    onCancel, 
    initialData = {},
    // Validation options
    requirePhone = true,
    requireEmail = false,
    requireRelation = false,
    relations = [],
    minAge = 18,
    maxAge = 60,
    context = 'beneficiary',
    // Form configuration
    showGender = true,
    showAge = true,
    showConfirmation = true,
    confirmationText = "Details provided herein are true and I have obtained all relevant organisational consents for the purposes.",
    // Button configuration
    saveButtonText = "Save & Continue",
    cancelButtonText = "Cancel",
    showCancelButton = true,
    // Custom fields
    additionalFields = [],
    // Callbacks
    onFieldChange,
    customValidation
}) => {
    const getInitialFormData = () => {
        const baseData = {
            full_name: initialData.full_name || "",
            dob: initialData.dob || "",
            age: initialData.age || "",
            gender: initialData.gender || "MALE",
            phone_number: initialData.phone_number || "",
            email: initialData.email || "",
            relation: initialData.relation || "",
            ocr_verified: false,
            id_number: null,
            document_type: null,
        };
        if (baseData.dob && !baseData.age) {
            const calculatedAge = getAge(baseData.dob);
            if (calculatedAge !== null) {
                baseData.age = calculatedAge;
            }
        }
        additionalFields.forEach(field => {
            if (field.defaultValue && !baseData[field.name]) {
                baseData[field.name] = field.defaultValue;
            }
        });
        return baseData;
    };
    
    const [formData, setFormData] = useState(getInitialFormData());
    
    const [validationErrors, setValidationErrors] = useState({});
    const [confirm, setConfirm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const { [field]: removed, ...rest } = prev;
                return rest;
            });
        }
        if (field === 'dob' && value) {
            const calculatedAge = getAge(value);
            if (calculatedAge !== null && calculatedAge >= 0) {
                setFormData(prev => ({
                    ...prev,
                    age: calculatedAge
                }));
                if (onFieldChange) {
                    onFieldChange('age', calculatedAge, { ...formData, [field]: value, age: calculatedAge });
                }
            }
        }
        if (onFieldChange) {
            onFieldChange(field, value, formData);
        }
    };

    const validateForm = () => {
        // Use custom validation if provided, otherwise use default
        if (customValidation) {
            const validation = customValidation(formData, confirm);
            setValidationErrors(validation.errors || {});
            return validation.isValid;
        }

        const validation = validateOCRForm(formData, {
            requirePhone,
            requireEmail,
            requireRelation,
            relations,
            minAge,
            maxAge,
            context
        });

        let errors = { ...validation.errors };

        // Confirmation validation
        if (showConfirmation && !confirm) {
            errors.confirm = "Please confirm the details to proceed";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (isSubmitting) {
            return; // Prevent multiple submissions
        }

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);  
        try {
            // Ensure age is calculated if DOB is provided
            const finalFormData = { ...formData };
            if (finalFormData.dob && !finalFormData.age) {
                const calculatedAge = getAge(finalFormData.dob);
                if (calculatedAge !== null) {
                    finalFormData.age = calculatedAge;
                }
            }
            const submissionData = {
                ...finalFormData,
                gender: finalFormData.gender || "MALE", // Ensure gender is never empty
                beneficiary_id: initialData.beneficiary_id || Date.now(),
                ocr_verified: false // Manual entry, not OCR verified
            };
            additionalFields.forEach(field => {
                if (finalFormData[field.name] !== undefined && finalFormData[field.name] !== '') {
                    submissionData[field.name] = finalFormData[field.name];
                }
            });
            await onSave(submissionData);
        } finally {
            setIsSubmitting(false);  
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
    };
    const allowEnglishOnly = (value) => value.replace(/[^\x00-\x7F]/g, '');

    return (
        <Box>
            <Grid container sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {requireRelation && (
                    <Grid item xs={12}>
                        <CustomSelect
                            id="relation-select"
                            label="Relation*"
                            value={formData.relation}
                            onChange={(e) => handleInputChange('relation', e.target.value)}
                            error={!!validationErrors.relation}
                            helperText={validationErrors.relation}
                            fullWidth
                        >
                            <MenuItem value="" disabled>
                                Choose relation
                            </MenuItem>
                            {relations.map((relationOption, index) => (
                                <MenuItem 
                                    key={index} 
                                    value={relationOption.value || relationOption}
                                >
                                    {relationOption.label || relationOption}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Grid>
                )}
                <Grid item xs={12}>
                      <TextField
                        label="Full Name*"
                        value={formData.full_name}
                        inputProps={{ inputMode: 'text' }}
                        onChange={(e) => {
                            const inputValue = e.target.value;
                            const hasNonEnglishChars = /[^\x00-\x7F]/.test(inputValue);
                            if (hasNonEnglishChars) {
                                setValidationErrors(prev => ({
                                    ...prev,
                                    full_name: "Only English characters allowed"
                                }));
                            } else {
                                setValidationErrors(prev => {
                                    const { full_name, ...rest } = prev;
                                    return rest;
                                });
                            }
                            const filteredValue = allowEnglishOnly(inputValue);
                            handleInputChange('full_name', filteredValue);
                        }}
                        error={!!validationErrors.full_name}
                        helperText={validationErrors.full_name} 
                        placeholder="Enter full name"
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={12}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Date of Birth*"
                            value={formData.dob ? dayjs(formData.dob) : null}
                            onChange={(newValue) => {
                                const dateString = newValue ? newValue.format('YYYY-MM-DD') : '';
                                handleInputChange('dob', dateString);
                            }}
                            error={!!validationErrors.dob}
                            helperText={validationErrors.dob}
                            disableFuture
                            format="DD/MM/YYYY"
                            fullWidth
                        />
                    </LocalizationProvider>
                </Grid>
                {showAge && (
                    <Grid item xs={12}>
                        <Typography variant='labelD' sx={{ color: '#666666'}}>Age</Typography>
                        <Typography variant='body1' sx={{ color: '#8C8C8C'}}>{formData.age !== '' && formData.age !== null && formData.age !== undefined ? formData.age : "-" }</Typography>
                        {validationErrors.age && (
                            <Typography variant='body1' sx={{ color: '#CC0000', fontSize: '13px', mt: 0.5 }}>
                                {validationErrors.age}
                            </Typography>
                        )}
                    </Grid>
                )}
                {showGender && (
                    <Grid item xs={12}>
                        <CustomSelect
                            id="gender-select"
                            label="Gender*"
                            value={formData.gender}
                            onChange={(e) => handleInputChange('gender', e.target.value)}
                            error={!!validationErrors.gender}
                            helperText={validationErrors.gender}
                            displayEmpty
                            fullWidth
                        >
                            <MenuItem value="" disabled>
                                Choose gender
                            </MenuItem>
                            <MenuItem value="MALE">Male</MenuItem>
                            <MenuItem value="FEMALE">Female</MenuItem>
                        </CustomSelect>
                    </Grid>
                )}
                
                {/* {additionalFields && additionalFields.map((field, index) => (
                    <Grid item xs={12} key={index}>
                        {field.type === 'text' && (
                            <TextField
                                label={field.label}
                                value={formData[field.name] || ''}
                                onChange={(e) => handleInputChange(field.name, e.target.value)}
                                error={!!validationErrors[field.name]}
                                helperText={validationErrors[field.name]}
                                placeholder={field.placeholder}
                                required={field.required}
                                fullWidth
                            />
                        )}
                        {field.type === 'select' && (
                            <CustomSelect
                                id={`${field.name}-select`}
                                label={field.label}
                                value={formData[field.name] || field.defaultValue || ''}
                                onChange={(e) => handleInputChange(field.name, e.target.value)}
                                error={!!validationErrors[field.name]}
                                helperText={validationErrors[field.name]}
                                required={field.required}
                                fullWidth
                                displayEmpty
                            >
                                <MenuItem value="" disabled>
                                    {field.placeholder || `Choose ${field.label.toLowerCase()}`}
                                </MenuItem>
                                {field.options.map((option, optIndex) => (
                                    <MenuItem 
                                        key={optIndex} 
                                        value={option.value || option}
                                    >
                                        {option.label || option}
                                    </MenuItem>
                                ))}
                            </CustomSelect>
                        )}
                    </Grid>
                ))} */}
                {requirePhone && (
                    <Grid item xs={12}>
                        <TextField
                            label="Phone Number*"
                            value={formData.phone_number}
                            onChange={(e) => handleInputChange('phone_number', e.target.value)}
                            error={!!validationErrors.phone_number}
                            helperText={validationErrors.phone_number}
                            placeholder="Enter 10-digit phone number"
                            inputProps={{ maxLength: 10 }}
                            fullWidth
                        />
                    </Grid>
                )}
                {requireEmail && (
                    <Grid item xs={12}>
                        <TextField
                            label="Email*"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            error={!!validationErrors.email}
                            helperText={validationErrors.email}
                            placeholder="Enter email address"
                            fullWidth
                        />
                    </Grid>
                )}
                {showConfirmation && (
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <CustomCheckbox 
                            label={confirmationText}
                            checked={confirm}
                            onChange={(e) => {
                                setConfirm(e.target.checked);
                                if (e.target.checked && validationErrors.confirm) {
                                    setValidationErrors(prev => {
                                        const { confirm, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                            sx={{ 
                                '&.MuiCheckbox-root':{
                                    py: 0,
                                }
                            }}
                        />
                        {validationErrors.confirm && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {validationErrors.confirm}
                            </Typography>
                        )}
                    </Grid>
                )}
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                {showCancelButton && (
                    <CustomButton
                        variant="outlined"
                        onClick={handleCancel}
                        label={cancelButtonText}
                        disabled={isSubmitting}
                    />
                )}
                <CustomButton
                    variant="filled"
                    onClick={handleSubmit}
                    label={saveButtonText}
                    fullWidth={!showCancelButton}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                />
            </Box>
        </Box>
    );
};

export default ReusableManualEntryForm;